import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { useUpdateNotificationMutation } from "@/redux/services/notification/notification.api";
import { Notification as NotificationType } from "@/redux/services/notification/notification.type";
import {
  addNotification,
  changeReadStatus as changeReadStatusAction,
} from "@/redux/slices/notification.slice";
import { notificationService } from "@/services/NotificationService";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { logger } from "@/services/Logger";

interface NotificationsContextType {
  notifications: NotificationType[];
  unreadCount: number;
  addNotification: (notification: NotificationType) => void;
  changeReadStatus: (id: string, isRead: boolean) => void;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(
    (state) => state.notification.notifications
  );
  const [updateNotification] = useUpdateNotificationMutation();

  const addNewNotification = useCallback(
    (notification: NotificationType) => {
      dispatch(addNotification(notification));
    },
    [dispatch]
  );

  const changeReadStatus = useCallback(
    async (id: string, isRead: boolean) => {
      try {
        await updateNotification({ id, isRead }).unwrap();
        dispatch(changeReadStatusAction({ id, isRead }));
      } catch (error) {
        logger.error("NotificationsContext", "Error changing notification status:", error);
      }
    },
    [dispatch, updateNotification]
  );

  // Handle service worker messages
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === "RECONNECT_MERCURE") {
          logger.info(
            "NotificationProvider",
            "Service worker requested Mercure reconnection"
          );
          notificationService.reconnect();
        } else if (event.data.type === "CONNECTION_RESTORED") {
          logger.info(
            "NotificationProvider",
            "Service worker reported connection restored"
          );
          notificationService.reconnect();
        } else if (event.data.type === "CONNECTION_LOST") {
          logger.info(
            "NotificationProvider",
            "Service worker reported connection lost"
          );
          // Connection will be automatically restored when online
        } else if (event.data.type === "CHECK_CONNECTION") {
          logger.info(
            "NotificationProvider",
            "Service worker requested connection check"
          );
          const status = notificationService.getConnectionStatus();
          if (!status.connected || !status.isServerConnectionAlive) {
            notificationService.reconnect();
          }
        } else if (event.data.type === "SW_KEEP_ALIVE_PING") {
          // Respond to service worker ping
          const status = notificationService.getConnectionStatus();
          navigator.serviceWorker.ready.then((registration) => {
            registration.active?.postMessage({
              type: "CONNECTION_STATUS",
              connected: status.connected && status.isServerConnectionAlive,
              timestamp: Date.now(),
            });
          });
        } else if (event.data.type === "KEEP_ALIVE_CHECK") {
          logger.info(
            "NotificationProvider",
            "Service worker keep-alive check"
          );
          // Send keep-alive signal back to service worker
          sendKeepAlive();
        }
      };

      navigator.serviceWorker.addEventListener("message", handleMessage);

      // Enhanced keep-alive function with message channel
      const sendKeepAlive = () => {
        navigator.serviceWorker.ready.then((registration) => {
          const channel = new MessageChannel();

          // Listen for acknowledgment
          channel.port1.onmessage = (event) => {
            if (event.data.type === "KEEP_ALIVE_ACK") {
              logger.debug(
                "NotificationProvider",
                "Keep-alive acknowledged by service worker"
              );
            }
          };

          registration.active?.postMessage(
            {
              type: "KEEP_ALIVE",
              timestamp: Date.now(),
            },
            [channel.port2]
          );
        });
      };

      // Send periodic keep-alive signals to service worker
      const keepAliveInterval = setInterval(sendKeepAlive, 30000); // Every 30 seconds

      // Send initial keep-alive
      sendKeepAlive();

      return () => {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
        clearInterval(keepAliveInterval);
      };
    }
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification: addNewNotification,
        changeReadStatus,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useUserNotifications must be used within a UserNotificationsProvider"
    );
  }

  return context;
};
